import { isDefined } from '@sgme/fp';
import { Button, HStack, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import { memo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { ExecutionStatus } from '@/App/tiles/shared/ExecutionStatus.tsx';
import CommercialMargin from '@/App/tiles/TargetAccumulator/CommercialMargin.tsx';
import TargetAccumulatorFooter from '@/App/tiles/TargetAccumulator/Footer/index.tsx';
import { HedgeAmount } from '@/App/tiles/TargetAccumulator/HedgeAmount.tsx';
import { HedgePaymentDate } from '@/App/tiles/TargetAccumulator/HedgePaymentDate';
import { HedgeType } from '@/App/tiles/TargetAccumulator/HedgeType.tsx';
import MarketData from '@/App/tiles/TargetAccumulator/MarketData/index.tsx';
import { PremiumDate } from '@/App/tiles/TargetAccumulator/PremiumDate.tsx';
import { PremiumPriceType } from '@/App/tiles/TargetAccumulator/PremiumPriceType.tsx';
import PricerView from '@/App/tiles/TargetAccumulator/PricerView.tsx';
import UnzoomedTileSchedulerView from '@/App/tiles/TargetAccumulator/Schedule/UnzoomedTileSchedulerView.tsx';
import ZoomedTileSchedulerView from '@/App/tiles/TargetAccumulator/Schedule/ZoomedTileSchedulerView';
import { SpotField } from '@/App/tiles/TargetAccumulator/SpotField.tsx';
import { SpotRef } from '@/App/tiles/TargetAccumulator/SpotRef.tsx';
import { useTargetAccumulatorSelector } from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorHeader } from '@/App/tiles/TargetAccumulator/TargetAccumulatorHeader.tsx';
import { TargetAccumulatorPriceSummary } from '@/App/tiles/TargetAccumulator/TargetAccumulatorPriceSummary/index.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { isNotDefined } from '@/components/sg-design-system/utils/index.ts';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabZoomedTileId, setZoomedTileIdForActiveTab } from '@/store/tabs/slice.ts';
import {
  selectDisplayMode,
  selectTargetAccumulatorCurrencyPair,
  selectTargetAccumulatorHedgeType,
  selectTargetAccumulatorTradeCaptureError,
} from '@/store/targetAccumulator/selectors.ts';
import { resetTargetAccumulatorThunk } from '@/store/targetAccumulator/thunk.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export type ForwardTileProps = {
  tileId: string;
};

export const TargetAccumulatorTile = ({ tileId }: ForwardTileProps) => {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const tileW = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));
  const currencyPair = useAppSelector((state) => selectTargetAccumulatorCurrencyPair(state, tileId));
  const hasCurrencyPair = isDefined(currencyPair);
  const hasTradeCaptureError = useAppSelector((state) =>
    isDefined(selectTargetAccumulatorTradeCaptureError(state, tileId)),
  );
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;

  const executionStatus = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator.execution?.status);

  function deZoom() {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'De-zoom from tile');
    dispatch(setZoomedTileIdForActiveTab({ tileId: undefined }));
  }
  function handleReset() {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Reset tile after error');
    dispatch(resetTargetAccumulatorThunk({ tileId }));
  }

  useEffect(() => {
    const newW = hasCurrencyPair ? 3 : 1;
    if (tileW !== newW) {
      dispatch(
        tilesSlice.actions.resizeTile({
          tileId,
          w: newW,
        }),
      );
    }
  }, [hasCurrencyPair, tileId, tileW]);

  if (hasTradeCaptureError) {
    return <ErrorTile tileId={tileId} onReset={handleReset} />;
  }

  if (isDefined(executionStatus)) {
    return <ExecutionStatus status={executionStatus} />;
  }

  return (
    <VStack fullWidth verticalAlignment="start" className="bg-lvl1" style={{ height: isZoomed ? '100%' : 'auto' }}>
      <AutoAdvanceForm className={isZoomed ? 'd-flex flex-column h-100' : undefined}>
        {isZoomed && (
          <div>
            <Button onClick={deZoom}>
              <em className="icon">arrow_back</em>
              <FormattedMessage id="zoomModeLayout.backToWorkspace" />
            </Button>
          </div>
        )}
        {isNotDefined(currencyPair) && (
          <div className="py-8px">
            <TargetAccumulatorHeader />
          </div>
        )}

        {isDefined(currencyPair) && (
          <HStack
            flex="expand"
            horizontalAlignment="start"
            verticalAlignment="start"
            className={clsx('h-100', { 'border-top pe-3': isZoomed })}
          >
            <LeftColumn tileId={tileId} isZoomed={isZoomed} />
            <RightPanel tileId={tileId} isZoomed={isZoomed} />
          </HStack>
        )}
      </AutoAdvanceForm>
    </VStack>
  );
};

type ColumnProps = { tileId: string; isZoomed: boolean };

const LeftColumn = memo(function LeftColumn({ tileId, isZoomed }: ColumnProps) {
  const hedgeType = useAppSelector((state) => selectTargetAccumulatorHedgeType(state, tileId));

  return (
    <div className="border-end align-self-stretch">
      <VStack fullHeight={!isZoomed}>
        <div className="py-8px">
          <TargetAccumulatorHeader />
          <SpotField />
          <SpotRef />
        </div>

        <TargetAccumulatorPriceSummary />

        <PremiumPriceType />
        <PremiumDate />
        <HedgeType />
        {hedgeType === 'Spot' && (
          <>
            <HedgePaymentDate />
            <HedgeAmount />
          </>
        )}
        <MarketData />

        <UsertypeGuard allow="Internal Sales">
          <CommercialMargin />
        </UsertypeGuard>
        <TargetAccumulatorFooter tileId={tileId} />
      </VStack>
    </div>
  );
});

const RightPanel = memo(function RightPanel({ tileId, isZoomed }: ColumnProps) {
  const displayMode = useAppSelector((state) => selectDisplayMode(state, tileId));

  if (displayMode === 'Pricer') {
    return <PricerView />;
  }
  if (displayMode === 'Scheduler') {
    return isZoomed ? <ZoomedTileSchedulerView /> : <UnzoomedTileSchedulerView />;
  }
  return null;
});
